import {useQuery} from '@apollo/client'
import {FormattedMessage, IntlShape, defineMessages, useIntl} from 'react-intl'
import React, {useEffect} from 'react'
import {GET_ALL_LOGS} from '../../../gql/queries/policiesApiQueries'
import {toast} from 'react-toastify'
import {Accordion, Spinner} from 'react-bootstrap'
import moment from 'moment'
import {formatPhoneDisplay} from 'utils/phoneUtils'
import {PhoneProviderActions} from '../../../components/telephony/PhoneProviderActions'

interface IActivityProps {
  id: string
  isQuote?: boolean
}

interface IResLogsPolicy {
  policyLogsByPolicyId: {
    id: string
    oldValue: string
    newValue: string
    updatedAt: string
    user: {
      id: string
      firstName: string
      lastName: string
    }
    changes: string[]
  }[]
}

const toDict = (val: any) => {
  if (val == null) return {}
  if (typeof val === 'string') {
    try {
      return JSON.parse(val)
    } catch (e) {
      return val // return raw string so we can detect plain-message
    }
  }
  if (typeof val === 'object') return val
  return {}
}

const isPlainMessage = (oldVal: any, newVal: any) => {
  // plain message if either side is a non-JSON string
  return (typeof oldVal === 'string' && oldVal.trim() !== '') || (typeof newVal === 'string' && newVal.trim() !== '')
}

type DifferenceRow = {
  field: string
  oldValue: any
  newValue: any
  meta?: {
    type: 'member'
    changedKeys: string[]
  }
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

const notAvailable = (intl: IntlShape) => tr(intl, 'common.value.notAvailable', 'N/A')

const formatCurrency = (intl: IntlShape, value: any) => {
  const number = Number(value)
  if (Number.isFinite(number)) {
    return currencyFormatter.format(number)
  }
  return value ?? notAvailable(intl)
}

const ensureObject = (value: any) => {
  if (!value) return undefined
  if (typeof value === 'object') return value
  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch (err) {
      return undefined
    }
  }
  return undefined
}

/**
 * Llaves NUEVAS declaradas aquí para que el extractor las capture: se consumen a través de los
 * helpers `tr()`/`numbered()`, y el extractor no sigue wrappers (guía §17.12).
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const M = defineMessages({
  usesTobacco: {id: 'policies.detail.tab.activity.field.usesTobacco', defaultMessage: 'Uses Tobacco'},
  immigrationStatusCode: {
    id: 'policies.detail.tab.activity.field.immigrationStatusCode',
    defaultMessage: 'Immigration Status Code',
  },
  employerName: {
    id: 'policies.detail.tab.activity.field.employerName',
    defaultMessage: 'Employer Name {n}',
  },
  employerPhone: {
    id: 'policies.detail.tab.activity.field.employerPhone',
    defaultMessage: 'Employer Phone {n}',
  },
  positionOccupation: {
    id: 'policies.detail.tab.activity.field.positionOccupation',
    defaultMessage: 'Position/Occupation {n}',
  },
  annualIncomeN: {
    id: 'policies.detail.tab.activity.field.annualIncomeN',
    defaultMessage: 'Annual Income {n}',
  },
})

const tr = (intl: IntlShape, id: string, defaultMessage: string) =>
  intl.formatMessage({id, defaultMessage})

/** Label numerado de los campos de empleo/ingreso del log (p. ej. "Employer Name 2"). */
const numbered = (intl: IntlShape, id: string, defaultMessage: string, n: number) =>
  intl.formatMessage({id, defaultMessage}, {n})

const getMemberFieldLabels = (intl: IntlShape): Record<string, string> => {
  const employerName = (n: number) =>
    numbered(intl, 'policies.detail.tab.activity.field.employerName', 'Employer Name {n}', n)
  const employerPhone = (n: number) =>
    numbered(intl, 'policies.detail.tab.activity.field.employerPhone', 'Employer Phone {n}', n)
  const positionOccupation = (n: number) =>
    numbered(intl, 'policies.detail.tab.activity.field.positionOccupation', 'Position/Occupation {n}', n)
  const annualIncomeN = (n: number) =>
    numbered(intl, 'policies.detail.tab.activity.field.annualIncomeN', 'Annual Income {n}', n)
  return {
    first_name: tr(intl, 'common.field.firstName', 'First Name'),
    middle_name: tr(intl, 'common.field.middleName', 'Middle Name'),
    last_name: tr(intl, 'common.field.lastName', 'Last Name'),
    second_last_name: tr(intl, 'common.field.secondLastName', 'Second Last Name'),
    rol: tr(intl, 'policies.list.preview.table.role', 'Role'),
    relationship: tr(intl, 'policies.list.wizard.family.relationship', 'Relationship'),
    applicant: tr(intl, 'policies.list.preview.table.applicant', 'Applicant'),
    gender: tr(intl, 'common.field.gender', 'Gender'),
    date_birth: tr(intl, 'common.field.dateOfBirth', 'Date of Birth'),
    phone: tr(intl, 'common.field.phone', 'Phone'),
    email_address: tr(intl, 'common.field.email', 'Email'),
    country_birth: tr(intl, 'policies.detail.form.member.countryBirth', 'Country of birth'),
    marital_status: tr(intl, 'policies.detail.form.member.maritalStatus', 'Marital status'),
    annual_income1: annualIncomeN(1),
    annual_income2: annualIncomeN(2),
    annual_income3: annualIncomeN(3),
    ssn: tr(intl, 'policies.list.preview.table.ssn', 'SSN'),
    uses_tobacco: tr(intl, 'policies.detail.tab.activity.field.usesTobacco', 'Uses Tobacco'),
    immigration_status: tr(intl, 'policies.list.wizard.holder.immigrationStatus', 'Immigration Status'),
    immigration_status_code: tr(
      intl,
      'policies.detail.tab.activity.field.immigrationStatusCode',
      'Immigration Status Code'
    ),
    employer_name1: employerName(1),
    employer_name2: employerName(2),
    employer_name3: employerName(3),
    employer_phone1: employerPhone(1),
    employer_phone2: employerPhone(2),
    employer_phone3: employerPhone(3),
    position_occupation1: positionOccupation(1),
    position_occupation2: positionOccupation(2),
    position_occupation3: positionOccupation(3),
    annual_income: tr(intl, 'policies.detail.form.member.employment.annualIncome', 'Annual income'),
  }
}

const humanizeKey = (key: string) => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

const emDash = '—'
const dashSep = ' - '

const yesNo = (intl: IntlShape, value: boolean) =>
  value ? tr(intl, 'common.boolean.yes', 'Yes') : tr(intl, 'common.boolean.no', 'No')

const formatMemberFieldValue = (intl: IntlShape, key: string, value: any) => {
  if (value === null || value === undefined) return notAvailable(intl)
  if (value === '') return emDash
  if (typeof value === 'boolean') return yesNo(intl, value)
  if (typeof value === 'number') {
    if (key.includes('income')) return formatCurrency(intl, value)
    return value.toString()
  }
  if (typeof value === 'string') {
    if (key.includes('phone')) return formatPhoneDisplay(value)
    if (key.includes('date')) {
      return moment(value).isValid() ? moment(value).format('MM/DD/YYYY') : value
    }
    return value
  }
  return JSON.stringify(value, null, 2)
}

const areValuesEqual = (valueA: any, valueB: any) => {
  if (valueA === valueB) return true
  if (valueA === null || valueA === undefined || valueB === null || valueB === undefined) {
    return valueA === valueB
  }
  if (typeof valueA === 'object' && typeof valueB === 'object') {
    try {
      return JSON.stringify(valueA) === JSON.stringify(valueB)
    } catch (err) {
      return false
    }
  }
  return false
}

const getMemberChangedKeys = (oldMember: any, newMember: any) => {
  const oldParsed = ensureObject(oldMember) || {}
  const newParsed = ensureObject(newMember) || {}
  if (typeof oldParsed !== 'object' || typeof newParsed !== 'object') return []
  const keys = new Set([...Object.keys(oldParsed), ...Object.keys(newParsed)])
  const changed: string[] = []
  keys.forEach((key) => {
    const prev = (oldParsed as Record<string, any>)[key]
    const next = (newParsed as Record<string, any>)[key]
    if (!areValuesEqual(prev, next)) {
      changed.push(key)
    }
  })
  return changed
}

const getMemberDisplayName = (intl: IntlShape, member: any) => {
  const parsedMember = ensureObject(member)
  if (parsedMember && typeof parsedMember === 'object') {
    const name = [
      parsedMember.first_name,
      parsedMember.middle_name,
      parsedMember.last_name,
      parsedMember.second_last_name,
    ]
      .filter(Boolean)
      .join(' ')
    if (name) return name
    if (parsedMember.relationship)
      return intl.formatMessage(
        {id: 'policies.detail.tab.activity.memberWithRelationship', defaultMessage: 'Member ({relationship})'},
        {relationship: parsedMember.relationship}
      )
    if (parsedMember.id)
      return intl.formatMessage(
        {id: 'policies.detail.tab.activity.memberWithId', defaultMessage: 'Member {id}'},
        {id: parsedMember.id}
      )
  }
  return tr(intl, 'policies.detail.form.member.defaultName', 'Member')
}

const buildMemberChangeSummary = (intl: IntlShape, diff: DifferenceRow) => {
  const memberFieldLabels = getMemberFieldLabels(intl)
  const changedKeys = diff.meta?.changedKeys ?? []
  const labelList = changedKeys
    .map((key) => memberFieldLabels[key] ?? humanizeKey(key))
    .filter(Boolean)
  const uniqueLabels = Array.from(new Set(labelList))
  const name = getMemberDisplayName(intl, diff.newValue ?? diff.oldValue)
  if (uniqueLabels.length === 0) {
    return intl.formatMessage(
      {id: 'policies.detail.tab.activity.changesInMember', defaultMessage: 'Changes in member {name}'},
      {name}
    )
  }
  return intl.formatMessage(
    {
      id: 'policies.detail.tab.activity.changesInMemberFields',
      defaultMessage: 'Changes in member {name}: {fields}',
    },
    {name, fields: uniqueLabels.join(', ')}
  )
}

const formatChangeMessages = (
  intl: IntlShape,
  messages: string[] | undefined,
  memberDiffs: DifferenceRow[]
) => {
  if (!Array.isArray(messages)) return []
  const diffsQueue = [...memberDiffs]
  const formatted = messages.map((msg) => {
    if (typeof msg === 'string' && msg.toLowerCase().includes('member') && diffsQueue.length > 0) {
      const diff = diffsQueue.shift()!
      return buildMemberChangeSummary(intl, diff)
    }
    return msg
  })

  while (diffsQueue.length > 0) {
    formatted.push(buildMemberChangeSummary(intl, diffsQueue.shift()!))
  }

  return formatted.filter((msg) => typeof msg === 'string' && msg.trim() !== '')
}

const formatMemberSummary = (intl: IntlShape, member: any, changedKeys?: string[]) => {
  const memberFieldLabels = getMemberFieldLabels(intl)
  const parsedMember = ensureObject(member)
  const changeSet = changedKeys && changedKeys.length > 0 ? new Set(changedKeys) : undefined
  const na = notAvailable(intl)
  const colon = ':'

  if ((!parsedMember || typeof parsedMember !== 'object') && changeSet) {
    return (
      <div className='d-flex flex-column gap-1'>
        {Array.from(changeSet).map((key) => (
          <div key={key}>
            <strong>{(memberFieldLabels[key] ?? humanizeKey(key)) + colon}</strong> {na}
          </div>
        ))}
      </div>
    )
  }

  if (!parsedMember || typeof parsedMember !== 'object') {
    return parsedMember ? String(parsedMember) : na
  }

  const handledKeys = new Set<string>()
  const fullName = [
    parsedMember.first_name,
    parsedMember.middle_name,
    parsedMember.last_name,
    parsedMember.second_last_name,
  ]
    .filter(Boolean)
    .join(' ')

  const items: {label: string; value: React.ReactNode}[] = []

  const addItem = (keys: string | string[], label: string, value: React.ReactNode) => {
    if (value === null || value === undefined || value === '' || value === na) return
    const keyArray = Array.isArray(keys) ? keys : [keys]
    if (changeSet && !keyArray.some((key) => changeSet.has(key))) return
    keyArray.forEach((key) => handledKeys.add(key))
    items.push({label, value})
  }

  addItem(
    ['first_name', 'middle_name', 'last_name', 'second_last_name'],
    tr(intl, 'common.field.name', 'Name'),
    fullName
  )
  addItem('rol', memberFieldLabels.rol, parsedMember.rol)
  addItem('relationship', memberFieldLabels.relationship, parsedMember.relationship)
  if (typeof parsedMember.applicant === 'boolean') {
    addItem('applicant', memberFieldLabels.applicant, yesNo(intl, parsedMember.applicant))
  }
  addItem('gender', memberFieldLabels.gender, parsedMember.gender)
  if (parsedMember.date_birth) {
    addItem(
      'date_birth',
      memberFieldLabels.date_birth,
      moment(parsedMember.date_birth).isValid()
        ? moment(parsedMember.date_birth).format('MM/DD/YYYY')
        : parsedMember.date_birth
    )
  }
  addItem(
    'phone',
    memberFieldLabels.phone,
    parsedMember.phone ? (
      <PhoneProviderActions
        phone={parsedMember.phone}
        displayText={formatPhoneDisplay(parsedMember.phone)}
        className='d-inline-flex flex-column align-items-start'
      />
    ) : null
  )
  addItem('email_address', memberFieldLabels.email_address, parsedMember.email_address)
  addItem('country_birth', memberFieldLabels.country_birth, parsedMember.country_birth)
  addItem('marital_status', memberFieldLabels.marital_status, parsedMember.marital_status)

  ;['annual_income1', 'annual_income2', 'annual_income3'].forEach((key, idx) => {
    const amount = parsedMember[key]
    const formatted = formatCurrency(intl, amount)
    if (!changeSet && (formatted === na || formatted === '$0.00')) {
      return
    }
    addItem(key, memberFieldLabels[key], formatted)
  })

  if (changeSet) {
    changeSet.forEach((key) => {
      if (handledKeys.has(key)) return
      const label = memberFieldLabels[key] ?? humanizeKey(key)
      const rawValue = (parsedMember as Record<string, any>)[key]
      items.push({
        label,
        value: formatMemberFieldValue(intl, key, rawValue),
      })
    })
  }

  if (items.length === 0) {
    return <span>{JSON.stringify(parsedMember, null, 2)}</span>
  }

  return (
    <div className='d-flex flex-column gap-1'>
      {items.map(({label, value}) => (
        <div key={label}>
          <strong>{label + colon}</strong> {value}
        </div>
      ))}
    </div>
  )
}

const renderValue = (
  intl: IntlShape,
  field: string,
  value: any,
  meta?: DifferenceRow['meta']
): React.ReactNode => {
  if (meta?.type === 'member') {
    return formatMemberSummary(intl, value, meta.changedKeys)
  }

  if (value === null || value === undefined) return notAvailable(intl)
  if (typeof value === 'boolean') return yesNo(intl, value)
  if (typeof value === 'object') {
    return <pre className='mb-0'>{JSON.stringify(value, null, 2)}</pre>
  }

  return String(value)
}

export const ActivityTab: React.FC<IActivityProps> = ({id, isQuote}) => {
  const intl = useIntl()
  const {data, loading, error} = useQuery<IResLogsPolicy>(GET_ALL_LOGS, {
    variables: {policyId: Number(id)},
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    if (error)
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {message: error.message}
        )
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  if (loading || !data) {
    return (
      <div className='cardContainer'>
        <div className='kt-heading pb-2 custom-border-brand d-flex align-items-center justify-content-between'>
          <span>
            <FormattedMessage id='policies.detail.tab.activity.title' defaultMessage='Activity' />
          </span>
        </div>
        <div className='d-flex flex-center' style={{height: '40vh'}}>
          <Spinner animation='border' />
        </div>
      </div>
    )
  }

  if (data.policyLogsByPolicyId.length === 0) {
    return (
      <div className='cardContainer'>
        <div className='kt-heading pb-2 custom-border-brand d-flex align-items-center justify-content-between'>
          <span>
            <FormattedMessage id='policies.detail.tab.activity.title' defaultMessage='Activity' />
          </span>
        </div>
        <div className='d-flex flex-center' style={{height: '40vh'}}>
          <h4>
            <FormattedMessage
              id='policies.detail.tab.activity.empty'
              defaultMessage='There is no activity recorded at the moment'
            />
          </h4>
        </div>
      </div>
    )
  }

  const first = data.policyLogsByPolicyId[0]

  const restArr = data.policyLogsByPolicyId?.slice(1) || []

  const firstEventKey = `${1}`
  const firstIndexLabel = `${1}:`
  const firstDate = moment(first.updatedAt).format('YYYY-MM-DD HH:mm:ss')
  const firstUser = `${first.user.firstName} ${first.user.lastName}`
  const firstChangesText = Array.isArray(first.changes)
    ? first.changes.join(', ')
    : String(first.changes || emDash)

  return (
    <div className='policy-activity'>
      <div className='kt-heading kt-heading--md custom-border-brand'>
        {isQuote && (
          <span>
            <FormattedMessage id='policies.detail.tab.activity.quoteLogs' defaultMessage='Quote logs' />
          </span>
        )}
        {!isQuote && (
          <span>
            <FormattedMessage id='policies.detail.tab.activity.policyLogs' defaultMessage='Policy logs' />
          </span>
        )}
      </div>
      <Accordion defaultActiveKey={`${data.policyLogsByPolicyId.length}`}>
        {restArr.reverse().map((log, index) => {
          const {oldValue, newValue, updatedAt, user, changes} = log
          const oldParsed = toDict(oldValue)
          const newParsed = toDict(newValue)

          let differences: DifferenceRow[] = []

          if (!isPlainMessage(oldParsed, newParsed)) {
            const newObj = (typeof newParsed === 'object' && newParsed) ? newParsed as Record<string, any> : {}
            const oldObj = (typeof oldParsed === 'object' && oldParsed) ? oldParsed as Record<string, any> : {}
            const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)])

            // Detectar si advisor_paid cambió de false a true
            const advisorPaidChanged = oldObj['advisor_paid'] === false && newObj['advisor_paid'] === true

            differences = Array.from(allKeys).reduce((diff, key) => {
              const prev = oldObj[key]
              const next = newObj[key]

              if (areValuesEqual(prev, next)) {
                // Si advisor_paid cambió y este es el campo assigned_to, agregarlo aunque no haya cambiado
                if (advisorPaidChanged && key === 'assigned_to' && next) {
                  diff.push({
                    field: key,
                    oldValue: prev,
                    newValue: next,
                  })
                }
                return diff
              }

              if (key === 'member') {
                const changedMemberKeys = getMemberChangedKeys(prev, next)
                if (changedMemberKeys.length === 0) return diff
                diff.push({
                  field: key,
                  oldValue: prev,
                  newValue: next,
                  meta: {type: 'member', changedKeys: changedMemberKeys},
                })
                return diff
              }

              diff.push({
                field: key,
                oldValue: prev,
                newValue: next,
              })
              return diff
            }, [] as DifferenceRow[])

            differences = differences.filter((ob) => ob.field !== 'updated')
          }

          const memberDiffs = differences.filter((diff) => diff.meta?.type === 'member')
          const formattedChanges = formatChangeMessages(intl, changes, memberDiffs)
          const logIndexLabel = `${data.policyLogsByPolicyId.length - index}:`
          const logDate = moment(updatedAt).format('YYYY-MM-DD HH:mm:ss')
          const logUser = `${user.firstName} ${user.lastName}`
          const changesText =
            formattedChanges.length > 0
              ? formattedChanges.join(', ')
              : Array.isArray(changes)
                ? changes.join(', ')
                : emDash

          return (
            <Accordion.Item
              eventKey={`${data.policyLogsByPolicyId.length - index}`}
              key={log.id}
              className='log-entry'
            >
              <Accordion.Header className='fw-bold'>
                <span>{logIndexLabel}</span> <span>{logDate}</span><span>{dashSep}</span>
                <span className='fw-bold'>{logUser}</span>
              </Accordion.Header>
              <Accordion.Body>
                <p>
                  <strong>
                    <FormattedMessage
                      id='policies.detail.tab.activity.changesLabel'
                      defaultMessage='Changes:'
                    />
                  </strong> {changesText}
                </p>
                {isPlainMessage(toDict(oldValue), toDict(newValue)) ? (
                  <div className='text-muted'>
                    <FormattedMessage
                      id='policies.detail.tab.activity.noStructured'
                      defaultMessage='No structured changes to display.'
                    />
                  </div>
                ) : (
                  <table className='table table-striped'>
                    <thead>
                      <tr>
                        <th className='fw-bold'>
                          <FormattedMessage id='policies.detail.tab.activity.th.field' defaultMessage='Field' />
                        </th>
                        <th className='fw-bold'>
                          <FormattedMessage id='policies.detail.tab.activity.th.previousValue' defaultMessage='Previous Value' />
                        </th>
                        <th className='fw-bold'>
                          <FormattedMessage id='policies.detail.tab.activity.th.newValue' defaultMessage='New Value' />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {differences.length > 0 ? (
                        differences.map(({field, oldValue, newValue, meta}, i) => (
                          <tr key={i}>
                            <td>{field}</td>
                            <td>{renderValue(intl, field, oldValue, meta)}</td>
                            <td>{renderValue(intl, field, newValue, meta)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3}>
                            <FormattedMessage
                              id='policies.detail.tab.activity.noDifferences'
                              defaultMessage='There are no differences'
                            />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </Accordion.Body>
            </Accordion.Item>
          )
        })}
        <Accordion.Item eventKey={firstEventKey} className='log-entry'>
          <Accordion.Header className='font-bold'>
            <span>{firstIndexLabel}</span> <span>{firstDate}</span><span>{dashSep}</span>
            <span className='fw-bold'>{firstUser}</span>
          </Accordion.Header>
          <Accordion.Body>
            <p>
              <strong>
                <FormattedMessage
                  id='policies.detail.tab.activity.changesLabel'
                  defaultMessage='Changes:'
                />
              </strong> {firstChangesText}
            </p>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  )
}
